// NOTE: These components are copied from the app-console project.

import { Para } from 'components/Para';
import type { MediaNode } from 'shared/modelTypes/media';
import { /* useCallback,*/ useEffect, useMemo, useRef } from 'react';
//Usually I'd put *all* imports at the top
// but in this case to make it a little more modular, I haven't

export const BunnyMediaRenderer = ({ media, autoplay, library }: { media: MediaNode; autoplay?: boolean; library?: string }) => {
	return <>{renderPreview(media, autoplay, library)}</>;
};

const renderPreview = (media: MediaNode, autoplay: boolean = false, library: string = import.meta.env.VITE_CDN) => {
	if (![import.meta.env.VITE_CDN, import.meta.env.VITE_CDN_GAME].includes(library)) library = import.meta.env.VITE_CDN;
	const streamLibraryID =
		library == import.meta.env.VITE_CDN
			? import.meta.env.VITE_BUNNY_VIDEO_LIBRARY_ID
			: import.meta.env.VITE_BUNNY_VIDEO_GAME_LIBRARY_ID;
	const url = getStorageUrl(media, library);
	// const url = getStorageUrl(media, import.meta.env.VITE_CDN);
	const kind = media.kind;
	switch (kind) {
		case 'folder':
			return <Para>{__`This folder contains ${media.children?.length} file(s).`}</Para>;
		case 'image':
			return <ImagePreview src={url} />;
		case 'video':
			return <BunnyVideoPlayer videoId={media.videoId} autoplay={autoplay} libraryId={streamLibraryID} />;
		case 'audio':
			return <AudioPlayer src={url} autoPlay={autoplay} />;
		case 'text':
			return <TextPreview src={url} />;
		case 'uploading':
			return <Para>{__`This file is still uploading.`}</Para>;
		default: {
			// unreachable(kind, 'Unhandled media node kind: %');
		}
	}
};

//Image Preview

export function ImagePreview({ src }: { src: string }) {
	return (
		<div className="checkerboard my-2 w-fit">
			<img src={src} />
		</div>
	);
}

//Text Preview

import { LoaderCircle, TriangleAlert } from 'lucide-react';
import { AsyncView } from './AsyncView';
import { Code } from './Code';

export function TextPreview({ src }: { src: string }) {
	return (
		<AsyncView
			promise={fetch(src).then((r) => r.text())}
			loadingView={function (): React.ReactNode {
				return <LoaderCircle className="size-6 animate-spin" />;
			}}
			errorView={function (error: Error): React.ReactNode {
				return (
					<div className="inline-flex gap-2 rounded-md border border-red-500 bg-red-100 p-4 text-red-950">
						<TriangleAlert className="inline-block" />
						error: {error.message}
					</div>
				);
			}}
			successView={function (data): React.ReactNode {
				return <Code block>{data}</Code>;
			}}
		/>
	);
}

//Bunny video renderer
import type { HTMLAttributes } from 'react';

/**
 * Bunny Video Player component that embeds videos from Bunny Stream.
 * Documentation: https://docs.bunny.net/docs/stream-embedding-videos
 */
type BunnyVideoPlayerProps = {
	/** The ID of the video to play */
	videoId: string;
	/** Controls whether the video should start playing automatically. Due to browser restrictions on auto-play, this may not always work. */
	autoplay?: boolean;
	/** Controls whether the video files are preloaded. When set to `true`, the player immediately starts downloading the video so playback begins more quickly. */
	preload?: boolean;
	/** If set to `true`, the player starts in mute mode (no sound). This is often used for autoplay scenarios. */
	muted?: boolean;
	/** Replays the video automatically after it ends, creating a continuous loop. */
	loop?: boolean;
	/** Controls the default captions file that will be shown. By passing the caption code, the player will automatically load these captions. */
	captions?: string;
	/** Sets the video start time. Supports multiple formats: seconds/minutes/hours notation, `hh:mm:ss` format, or a simple numeric value interpreted as seconds. */
	startTime?: string | number;
	/** Enables or disables Chromecast support within the player. */
	chromecast?: boolean;
	/** Disables AirPlay support when set to `true`. */
	disableAirplay?: boolean;
	/** Disables the native iOS player (used typically for fullscreen handling on iOS). */
	disableIosPlayer?: boolean;
	/** Displays a heatmap on the progress bar when set to `true`, highlighting viewer engagement at different points in the video. */
	showHeatmap?: boolean;
	/** Allows the video to play inline on mobile devices rather than forcing fullscreen playback. */
	playsinline?: boolean;
	/** Shows a speed control within the player, allowing playback rate adjustments. */
	showSpeed?: boolean;
	/** Controls whether the player remembers the last playback position for the viewer within their browser. When set to `true`, the player stores the last position and attempts to resume from that point when the viewer returns. When `false`, the video always starts from the beginning. */
	rememberPosition?: boolean;
	/** The aspect ratio of the video, e.g. `4 / 3`. Default is `16 / 9` */
	aspectRatio?: number;

	/** Wether the player will take up the full screen */
	fullScreen?: boolean;

	/** Controls whether the player should be responsive to its container size, overrides aspectRatio if true */
	responsive?: boolean;

	// Bunny Library ID
	libraryId?: number;

	/** Event callbacks */
	onPlay?: () => void;
	onPause?: () => void;
	onEnded?: () => void;
	onSeeked?: () => void;
	onError?: (error: unknown) => void;
} & HTMLAttributes<HTMLDivElement>;

export const BunnyVideoPlayer = ({
	videoId,
	autoplay = false,
	loop = false,
	muted = false,
	preload = true,
	responsive = true,
	captions,
	startTime,
	chromecast,
	disableAirplay,
	disableIosPlayer,
	showHeatmap,
	playsinline,
	showSpeed,
	rememberPosition,
	aspectRatio = 16 / 9,
	fullScreen = false,
	libraryId,
	// onPlay,
	// onPause,
	// onEnded,
	// onSeeked,
	// onError,
	...props
}: BunnyVideoPlayerProps) => {
	if (!libraryId) {
		libraryId = import.meta.env.VITE_BUNNY_VIDEO_LIBRARY_ID;
	}
	const iframeRef = useRef<HTMLIFrameElement>(null);

	if (!libraryId) {
		throw new Error('BUNNY_VIDEO_LIBRARY_ID is not set');
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	// const player = useRef<any>(null);

	// const onReady = useCallback(() => {
	// 	if (onPlay) player.current.on('play', onPlay);
	// 	if (onPause) player.current.on('pause', onPause);
	// 	if (onEnded) player.current.on('ended', onEnded);
	// 	if (onSeeked) player.current.on('seeked', onSeeked);
	// 	if (onError) player.current.on('error', onError);
	// }, [onPlay, onPause, onEnded, onSeeked, onError]);

	// const [ready, setReady] = useState(false);

	// Set up event listeners for the Bunny.net player
	// useEffect(() => {
	// 	if (!ready) {
	// 		setReady(true);
	// 		return;
	// 	}

	// 	const iframe = iframeRef.current;
	// 	if (!iframe) return;

	// 	if (player.current) return;

	// 	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	// 	player.current = new (window as any).playerjs.Player(iframe);

	// 	player.current.on('ready', onReady);

	// 	return () => {
	// 		if (!player.current) return;

	// 		// try {
	// 		// 	if (onReady) player.current?.off('ready', onReady);
	// 		// 	if (onPlay) player.current?.off('play', onPlay);
	// 		// 	if (onPause) player.current?.off('pause', onPause);
	// 		// 	if (onEnded) player.current?.off('ended', onEnded);
	// 		// 	if (onSeeked) player.current?.off('seeked', onSeeked);
	// 		// 	if (onError) player.current?.off('error', onError);
	// 		// 	player.current = null;
	// 		// } catch (e) {
	// 		// 	console.warn('Error unbinding player events', e);
	// 		// }
	// 	};
	// }, [onPlay, onPause, onEnded, onSeeked, onError, onReady, ready]);

	const [_ready, setReady] = useState(false);
	const [refreshToken, setRefreshToken] = useState<number | null>(null);

	async function isBunnyReady() {
		const r = await fetch(import.meta.env.VITE_WEBSOCKET_URL + `/bunny-video-ready?videoId=${videoId}`);
		// 204 == ready, 202 == processing
		return r.status === 204;
	}

	useEffect(() => {
		let canceled = libraryId == import.meta.env.VITE_BUNNY_VIDEO_LIBRARY_ID; // false;
		let delay = 1000;

		if (canceled) return;
		(async function poll() {
			for (let i = 0; i < 6 && !canceled; i++) {
				const ok = await isBunnyReady().catch(() => false);
				if (ok) {
					setReady(true);
					setRefreshToken(Date.now());
					return;
				}
				await new Promise((r) => setTimeout(r, delay));
				delay *= 2;
			}
		})();

		return () => {
			canceled = true;
		};
	}, [libraryId, videoId]);

	const urlParams = new URLSearchParams({
		autoplay: autoplay ? 'true' : 'false',
		loop: loop.toString(),
		muted: muted.toString(),
		preload: preload.toString(),
		responsive: responsive.toString(),
		...(captions && { captions }),
		...(startTime && { t: startTime.toString() }),
		...(chromecast !== undefined && { chromecast: chromecast.toString() }),
		...(disableAirplay !== undefined && { disableAirplay: disableAirplay.toString() }),
		...(disableIosPlayer !== undefined && { disableIosPlayer: disableIosPlayer.toString() }),
		...(showHeatmap !== undefined && { showHeatmap: showHeatmap.toString() }),
		...(playsinline !== undefined && { playsinline: playsinline.toString() }),
		...(showSpeed !== undefined && { showSpeed: showSpeed.toString() }),
		...(rememberPosition !== undefined && { rememberPosition: rememberPosition.toString() }),
		t: refreshToken ? refreshToken?.toString() : 'null',
	});

	const aspectRatioString = (1 / aspectRatio) * 100 + '%';

	const src = useMemo(() => {
		// Add date to url to rerender component
		const base = `https://player.mediadelivery.net/embed/${libraryId}/${videoId}?${urlParams.toString()}`;
		return refreshToken ? `${base}?t=${refreshToken}` : base;
	}, [libraryId, videoId, refreshToken]);

	const iframe = (
		<iframe
			key={videoId}
			ref={iframeRef}
			src={src}
			loading="lazy"
			style={{ border: '0', position: 'absolute', top: '0', height: '100%', width: '100%' }}
			allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;fullscreen;"
			allowFullScreen={true}
		></iframe>
	);

	return (
		<div {...props}>
			{fullScreen ? (
				iframe
			) : (
				<div style={{ position: 'relative', paddingTop: aspectRatioString }} className="overflow-hidden rounded-lg bg-black">
					{iframe}
				</div>
			)}
		</div>
	);
};

//Audio renderer
import { Pause, Play } from 'lucide-react';
import { useState } from 'react';
import { getStorageUrl } from 'shared/util';

const THEME = {
	colors: {
		played: '#d946ef',
		unplayed: '#cbd5e1',
	},
	waveform: {
		barCount: 134,
		height: 100,
		maxHeightRatio: 0.8,
		minBarHeight: 2,
	},
} as const;

// Create a wrapper component that forces remounting
// If we don't do this some of the state will be preserved when we render a new AudioPlayer with a different src
export function AudioPlayer({ src, autoPlay }: { src: string; autoPlay?: boolean }) {
	return <AudioPlayerInternal key={src} src={src} autoPlay={autoPlay} />;
}

function AudioPlayerInternal({ src, autoPlay }: { src: string; autoPlay?: boolean }) {
	// NOTE: component largely generated by cursor to be "good enough" for now.
	// if we want a better audio player this code should be cleaned up first before adding more features.

	const audioRef = useRef<HTMLAudioElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [_isPlaying, setIsPlaying] = useState(false);
	const [progress, setProgress] = useState(0);
	const [waveform, setWaveform] = useState<number[]>([]);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;

		// Generate waveform data
		const audioContext = new AudioContext();
		fetch(src)
			.then((response) => response.arrayBuffer())
			.then((arrayBuffer) => audioContext.decodeAudioData(arrayBuffer))
			.then((audioBuffer) => {
				const channelData = audioBuffer.getChannelData(0);
				const samples = THEME.waveform.barCount;
				const blockSize = Math.floor(channelData.length / samples);
				const waveformData = [];

				// First pass: find the maximum amplitude
				let maxAmplitude = 0;
				for (let i = 0; i < samples; i++) {
					const start = blockSize * i;
					let sum = 0;
					for (let j = 0; j < blockSize; j++) {
						sum += Math.abs(channelData[start + j]);
					}
					const avg = sum / blockSize;
					maxAmplitude = Math.max(maxAmplitude, avg);
				}

				// Second pass: normalize the values
				for (let i = 0; i < samples; i++) {
					const start = blockSize * i;
					let sum = 0;
					for (let j = 0; j < blockSize; j++) {
						sum += Math.abs(channelData[start + j]);
					}
					const avg = sum / blockSize;
					// Normalize against maxAmplitude, ensuring we don't divide by zero
					waveformData.push(maxAmplitude > 0 ? avg / maxAmplitude : 0);
				}

				setWaveform(waveformData);
			});

		const updateProgress = () => {
			setProgress((audio.currentTime / audio.duration) * 100);
		};

		audio.addEventListener('timeupdate', updateProgress);
		audio.addEventListener('play', () => setIsPlaying(true));
		audio.addEventListener('pause', () => setIsPlaying(false));

		return () => {
			audio.removeEventListener('timeupdate', updateProgress);
			audio.removeEventListener('play', () => setIsPlaying(true));
			audio.removeEventListener('pause', () => setIsPlaying(false));
		};
	}, [src]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas || waveform.length === 0) return;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const width = canvas.width;
		const height = canvas.height;
		const barWidth = width / waveform.length;
		const maxHeight = height * THEME.waveform.maxHeightRatio;

		ctx.clearRect(0, 0, width, height);

		waveform.forEach((value, index) => {
			const barHeight = Math.max(value * maxHeight, THEME.waveform.minBarHeight);
			const x = index * barWidth;
			const y = (height - barHeight) / 2;

			// Color based on progress
			const progressPoint = (progress / 100) * waveform.length;
			ctx.fillStyle = index < progressPoint ? THEME.colors.played : THEME.colors.unplayed;

			ctx.fillRect(x, y, barWidth - 1, barHeight);
		});
	}, [waveform, progress]);

	const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
		const audio = audioRef.current;
		const canvas = canvasRef.current;
		if (!audio || !canvas) return;

		const rect = canvas.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const percentage = x / rect.width;
		audio.currentTime = audio.duration * percentage;
	};

	return (
		<div className="flex items-center gap-2 rounded-lg bg-white p-4 shadow-md">
			<div className="flex justify-center gap-2">
				<button
					onClick={() => (_isPlaying ? audioRef.current?.pause() : audioRef.current?.play())}
					className={`rounded-full bg-gray-900 p-3 text-gray-200 transition-colors`}
				>
					{_isPlaying && <Pause className="size-5" />}
					{!_isPlaying && <Play className="size-5" />}
				</button>
			</div>
			<audio ref={audioRef} src={src} autoPlay={autoPlay} />
			<canvas
				data-loading={waveform.length == 0 ? '' : undefined}
				ref={canvasRef}
				width={800}
				height={THEME.waveform.height}
				onClick={handleCanvasClick}
				className="h-[100px] min-w-0 flex-1 shrink cursor-pointer rounded-md data-[loading]:animate-pulse data-[loading]:bg-gray-100"
			/>
		</div>
	);
}
